<template>
  <div class="condition-editor pbd-editor" :data-test-id="dataTestId">
    <div class="condition-editor-header">
      <span class="pbd-type-label">PositionBasedDiscount</span>
      <v-spacer />
      <v-btn
        v-if="!disabled"
        icon
        color="red"
        :data-test-id="dataTestId + '_remove_btn'"
        @click="$emit('delete')"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </div>

    <div class="condition-editor-content">
      <v-row dense>
        <v-col>
          <v-text-field
            v-model="config.packageSku"
            dense
            outlined
            label="Package SKU*"
            hide-details="auto"
            :disabled="disabled"
            :rules="[required]"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="config.productSku"
            dense
            outlined
            label="Product SKU*"
            hide-details="auto"
            :disabled="disabled"
            :rules="[required]"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="config.sortOrder"
            dense
            outlined
            attach
            label="Sort Order"
            hide-details="auto"
            :items="sortOrderOptions"
            :disabled="disabled"
          />
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="2">
          <v-text-field
            v-model.number="rounding.precision"
            dense
            outlined
            type="number"
            label="Rounding precision"
            hide-details="auto"
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="rounding.mode"
            dense
            outlined
            attach
            label="Rounding mode"
            hide-details="auto"
            :items="roundingModeOptions"
            :disabled="disabled"
          />
        </v-col>
      </v-row>

      <div class="overline mt-3 mb-1 px-1">Positions</div>
      <v-simple-table dense class="positions-table">
        <thead>
          <tr>
            <th>From*</th>
            <th>To</th>
            <th>Type*</th>
            <th>Value</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(pos, pi) in config.positions" :key="pi">
            <td>
              <v-text-field
                v-model.number="pos.from"
                dense
                type="number"
                hide-details
                min="0"
                :disabled="disabled"
                style="width: 60px"
              />
            </td>
            <td>
              <v-text-field
                v-model.number="pos.to"
                dense
                type="number"
                hide-details
                clearable
                min="0"
                placeholder="∞"
                :disabled="disabled"
                style="width: 60px"
                @click:clear="clearTo(pos)"
              />
            </td>
            <td>
              <v-select
                v-model="pos.type"
                dense
                hide-details
                :items="discountTypeOptions"
                :disabled="disabled"
                style="width: 130px"
                @change="onTypeChange(pos)"
              />
            </td>
            <td class="value-cell">
              <template v-if="pos.type === 'FIXED'">
                <v-text-field
                  v-model.number="pos.amount"
                  dense
                  type="number"
                  hide-details
                  placeholder="Amount"
                  :disabled="disabled"
                  style="width: 90px; display: inline-flex"
                />
                <v-text-field
                  v-model="pos.currency"
                  dense
                  hide-details
                  placeholder="Currency"
                  :disabled="disabled"
                  style="width: 70px; display: inline-flex; margin-left: 6px"
                />
              </template>
              <v-text-field
                v-else-if="pos.type === 'RELATIVE'"
                v-model.number="pos.percent"
                dense
                type="number"
                hide-details
                suffix="%"
                :disabled="disabled"
                style="width: 90px"
              />
            </td>
            <td>
              <v-btn
                v-if="!disabled"
                icon
                x-small
                color="red"
                @click="removePosition(pi)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <v-btn
        v-if="!disabled"
        text
        small
        color="primary"
        class="mt-1"
        @click="addPosition"
      >
        <v-icon small>mdi-plus</v-icon> Add Position
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "PositionBasedDiscountEditor",

  props: {
    value: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dataTestId: {
      type: String,
      default: "pbd-editor"
    }
  },

  data() {
    const cfg = this.$cloneObject(this.value?.configuration ?? {});
    if (!cfg.positions) cfg.positions = [];
    if (!cfg.sortOrder) cfg.sortOrder = "NONE";
    const rounding = cfg.rounding ?? { precision: 2, mode: "roundHalfUp" };
    return {
      config: cfg,
      rounding
    };
  },

  watch: {
    config: {
      deep: true,
      handler() {
        this.emit();
      }
    },
    rounding: {
      deep: true,
      handler(r) {
        this.config.rounding = r;
      }
    }
  },

  methods: {
    emit() {
      this.$emit("input", {
        ...this.value,
        configuration: { ...this.config, rounding: this.rounding }
      });
    },

    addPosition() {
      this.config.positions.push({ from: 1, type: "NONE" });
    },

    removePosition(idx) {
      this.config.positions.splice(idx, 1);
    },

    onTypeChange(pos) {
      this.$delete(pos, "amount");
      this.$delete(pos, "currency");
      this.$delete(pos, "percent");
    },

    clearTo(pos) {
      this.$delete(pos, "to");
    },

    required: v => !!v || "Required"
  },

  computed: {
    sortOrderOptions() {
      return ["NONE", "CHEAPEST_FIRST", "MOST_EXPENSIVE_FIRST"];
    },
    discountTypeOptions() {
      return ["NONE", "FIXED", "RELATIVE"];
    },
    roundingModeOptions() {
      return ["roundHalfUp", "alwaysRoundUp", "alwaysRoundDown"];
    }
  }
};
</script>

<style scoped>
.pbd-editor .pbd-type-label {
  font-weight: 500;
  font-size: 0.875rem;
}

.pbd-editor .positions-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.pbd-editor .positions-table thead th {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.pbd-editor .positions-table .value-cell {
  white-space: nowrap;
}
</style>
